import React, { memo, useEffect, useState } from 'react';
import CancelIcon from 'components/svg/cancel.svg';
import { Grid, SvgIcon, Theme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'i18n';
import nodeMap from 'utils/nodeMap';
import { useQueryParams } from 'hooks';

import Button from 'components/common/Button';
import { FilterNameProvider } from './FilterNameProvider';

export interface IFilterPanelProps {
  isMobile?: boolean;
  sort?: React.ReactNode;
  children?: React.ReactNode;
  search?: React.ReactNode;
  withoutCloseBtn?: boolean;
}

const FilterPanel: React.FC<React.PropsWithChildren<IFilterPanelProps>> = (props) => {
  const { search, sort, isMobile, children, withoutCloseBtn } = props;
  const [t] = useTranslation();
  const [filtersName, setFiltersName] = useState<{ [key: string]: string[] }>({});
  const isLessSM = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const hasFilters = () => React.Children.count(children) > 0;
  const [queryParams, setQueryParams] = useQueryParams();
  const hasFiltersNames = () => Object.values(filtersName).some((arr) => arr.some((name) => queryParams[name]));

  const handleClearQuery: React.MouseEventHandler = () => {
    const value = Object.values(filtersName).reduce((obj, arr) => {
      arr.forEach((name) => {
        if (queryParams[name]) obj[name] = undefined;
      });
      return obj;
    }, {} as { [key: string]: string[] });
    if (search) {
      value.search = undefined;
    }
    setQueryParams({
      ...queryParams,
      ...value,
    });
  };

  useEffect(
    () => () => {
      setFiltersName({});
    },
    [],
  );

  return (
    <FilterNameProvider filtersName={filtersName} setFiltersName={setFiltersName}>
      <div>
        {search && (
          <Grid container justifyContent="space-between" alignItems="flex-start" spacing={3} xs={12} sx={{ m: 0 }}>
            {search}
          </Grid>
        )}
        <Grid container justifyContent="space-between" alignItems="center" spacing={3}>
          <Grid item xs>
            {hasFilters() && (
              <Grid container alignItems="center" spacing={1}>
                {nodeMap(children, (child) => (
                  <Grid item={!isMobile} container={isMobile}>
                    {child}
                  </Grid>
                ))}
                {hasFiltersNames && !withoutCloseBtn && (
                  <Grid item>
                    <Button
                      size="small"
                      color="secondary"
                      sx={{ p: 0, pl: 1 }}
                      startIcon={<SvgIcon color="secondary" component={CancelIcon} viewBox="0 0 16 16" />}
                      onClick={handleClearQuery}
                    >
                      {t<string>('common:button.clearAll')}
                    </Button>
                  </Grid>
                )}
                {sort && isLessSM && (
                  <Grid container justifyContent="flex-end" spacing={3}>
                    <Grid item>{sort}</Grid>
                  </Grid>
                )}
              </Grid>
            )}
          </Grid>
          {sort && (!isLessSM || !hasFilters()) && <Grid item>{sort}</Grid>}
        </Grid>
      </div>
    </FilterNameProvider>
  );
};

export default memo(FilterPanel);
